import {
    ENVIRONMENT_VARIABLE_KEY_DBXLURL,
    ENVIRONMENT_VARIABLE_KEY_DBXLUSERKEY,
} from '../../../constants/environmentVariable.constants';
import {
    DBXL_URL_VARIABLE_TOKEN,
    DBXL_USER_KEY_VARIABLE_TOKEN,
} from '../../helpers/valueTokens';
import {
    paramKey,
    paramUrl,
    paramUseKeyVariable,
    paramUseUrlVariable,
} from '../constants/parameterNames';
import { typeBoolean } from '../constants/parameterTypes';
import {
    changeServiceUrlBase,
    isDbxlDataConnection,
    isUserKeyNamespace,
} from '../helpers/dbxl.helpers';
import {
    getBoolParamValue,
    getParamValue,
    getParamValueOrEnvironmentVariable,
} from '../helpers/parameter.accessors';
import {
    enumerateDataConnections,
    getDataConnection,
} from '../helpers/qRules.dataConnection.helpers';
import qRulesNamespaceResolver from '../helpers/qRulesNamespaceResolver';

const description =
    'Repoints DBXL data connections to a new location, and sets their user key values.\n\nAllows specifying DBXL URL and User Key via parameters or environment variables.';

const optionalParameters = {
    [paramUrl]: {
        description: `DBXL base URL.\n\nAny DBXL web connections found in the form will be repointed to this URL.\n\nExample: https://dbxl.mycompany.com/QdabraWebService\n\nIf unspecified, command will look for an environment variable named ${ENVIRONMENT_VARIABLE_KEY_DBXLURL}\n\nEither a URL or a User Key must be specified via parameter or environment variable.`,
    },
    [paramKey]: {
        description: `A DBXL user key.\n\nWill be placed in the key query field of any user key-based DBXL connections.\n\nIf unspecified, command will look for an environment variable named ${ENVIRONMENT_VARIABLE_KEY_DBXLUSERKEY}\n\nEither a URL or a User Key must be specified via parameter or environment variable.`,
    },
    [paramUseUrlVariable]: {
        description: `(boolean, defaults to false) When true, indicates that the command should use an Environment Variable for the DBXL url.\n\nIf a ${ENVIRONMENT_VARIABLE_KEY_DBXLURL} variable is found, its value will be used. Otherwise, a variable token for the ${ENVIRONMENT_VARIABLE_KEY_DBXLURL} variable will be used.\n\nIn the latter case, the ${ENVIRONMENT_VARIABLE_KEY_DBXLURL} environment variable must have a slash at the end of its value (e.g. https://dbxl.mycompany.com/).\n\nIf false, the command will use a ${ENVIRONMENT_VARIABLE_KEY_DBXLURL} environment variable if one is found, but will not use a variable token.`,
        type: typeBoolean,
    },
    [paramUseKeyVariable]: {
        description: `(boolean, defaults to false) When true, a variable token for the variable ${ENVIRONMENT_VARIABLE_KEY_DBXLUSERKEY} will be set for connections' userkeys.\n\nIf false, the command will use a ${ENVIRONMENT_VARIABLE_KEY_DBXLUSERKEY} environment variable if one is found, but will not use a variable token.`,
        type: typeBoolean,
    },
};

// (DataSourceCollection, DataConnection, String) -> Promise[Boolean]
const trySetUserKey = async (dataSources, conn, userKey) => {
    const { name } = conn;

    if (!dataSources.hasDataSource(name)) {
        return false;
    }

    const source = dataSources.getDom(name);

    const queryRoot = source.selectSingle('/dfs:myFields/dfs:queryFields/*', {
        namespaces: qRulesNamespaceResolver,
    });

    const namespace = queryRoot.namespaceURI();

    if (isUserKeyNamespace(namespace)) {
        // [Jimmy] As of 2022-08-15, all userKey methods use a parameter named
        //         key for the userkey.
        const keyField = queryRoot.selectSingle('*[local-name() = "key"]');

        if (keyField) {
            await keyField.setValueAsync(userKey);

            return true;
        }
    }

    return false;
};

const updateUrl = (conn, dbxlUrl) => {
    const url = conn.getUrl();

    if (!url) {
        console.warn(
            `Could not determine url for DBXL connection ${conn.name}`,
        );

        return false;
    }

    if (!dbxlUrl) {
        return false;
    }

    const newUrl = changeServiceUrlBase(url, dbxlUrl);

    conn.setUrl(newUrl);

    return true;
};

const updateKey = async (dataSources, conn, userKey) => {
    if (userKey) {
        return await trySetUserKey(dataSources, conn, userKey);
    }

    return false;
};

const updateDbxlConnection = async (dataSources, conn, dbxlUrl, userKey) => {
    const urlUpdated = updateUrl(conn, dbxlUrl);

    const keyUpdated = await updateKey(dataSources, conn, userKey);

    return urlUpdated || keyUpdated;
};

/**
 * Determines the DBXL URL to use based on parameters, environment variables, and useUrlVariable flag
 * @param {ParameterCollection} parameters - Command parameters
 * @param {Settings} settings - Environment settings
 * @returns {string|null} - The DBXL URL to use, or null if none available
 */
export const determineDbxlUrl = (parameters, settings) => {
    // Get the URL parameter value if specified
    const urlValue = getParamValue(parameters, paramUrl);
    const urlSpecified = !!urlValue;

    // Check if useUrlVariable parameter is true
    const useUrlVariable = getBoolParamValue(
        parameters,
        paramUseUrlVariable,
        false,
    );

    // Get environment variable value at the beginning
    const envVariable = settings.getEnvironmentVariable(
        ENVIRONMENT_VARIABLE_KEY_DBXLURL,
    );
    const envValue = envVariable?.value;

    // Can't specify both url parameter and useUrlVariable
    if (urlSpecified && useUrlVariable) {
        throw new Error(
            `Cannot specify both /${paramUrl} and /${paramUseUrlVariable} parameters at the same time.`,
        );
    }

    // If useUrlVariable is true, use the environment variable value if available, otherwise use the token
    if (useUrlVariable) {
        return envValue || DBXL_URL_VARIABLE_TOKEN;
    }

    // Try url parameter first, then environment variable
    return urlValue || envValue || null;
};

/**
 * Determines the DBXL User Key to use based on parameters, environment variables, and useUserKeyVariable flag
 * @param {ParameterCollection} parameters - Command parameters
 * @param {Settings} settings - Environment settings
 * @returns {string|null} - The DBXL User Key to use, or null if none available
 */
export const determineUserKey = (parameters, settings) => {
    // Check if key parameter is specified (using truthy check)
    const keyValue = getParamValue(parameters, paramKey);
    const keySpecified = !!keyValue;

    // Check if useUserKeyVariable parameter is true
    const useUserKeyVariable = getBoolParamValue(
        parameters,
        paramUseKeyVariable,
        false,
    );

    // Can't specify both
    if (keySpecified && useUserKeyVariable) {
        throw new Error(
            `Cannot specify both /${paramKey} and /${paramUseKeyVariable} parameters at the same time.`,
        );
    }

    // If useUserKeyVariable is true, use the variable token
    if (useUserKeyVariable) {
        return DBXL_USER_KEY_VARIABLE_TOKEN;
    }

    // Try to get the User Key from parameters or environment variables
    return getParamValueOrEnvironmentVariable(
        parameters,
        paramKey,
        settings,
        ENVIRONMENT_VARIABLE_KEY_DBXLUSERKEY,
    );
};

const executeAsync = async ({
    parameters,
    dataConnections,
    dataSources,
    settings,
}) => {
    const dbxlUrl = determineDbxlUrl(parameters, settings);
    const userKey = determineUserKey(parameters, settings);

    if (!dbxlUrl && !userKey) {
        throw new Error(
            `Must specify at least one parameter relating to DBXL URL or User Key, or one of these must be available via Environment Variables.`,
        );
    }

    let updateCount = 0;
    const dataConnectionNames = enumerateDataConnections(dataConnections);

    for (const name of dataConnectionNames) {
        const conn = getDataConnection(dataConnections, name);

        if (isDbxlDataConnection(conn)) {
            const updated = await updateDbxlConnection(
                dataSources,
                conn,
                dbxlUrl,
                userKey,
            );

            if (updated) {
                updateCount += 1;
            }
        }
    }

    return updateCount;
};

const AdjustDbxlConnections = {
    description,
    optionalParameters,
    executeAsync,
};

export default AdjustDbxlConnections;
